const ORG_BASE = {
    "@type": "Organization",
    "@id": "https://www.aia.edu.sg/#organization",
    "name": "Ace Industrial Academy",
    "url": "https://www.aia.edu.sg",
    "logo": "https://www.aia.edu.sg/icons/icon-512x512.png",
    "description": "Ace Industrial Academy is the leading BIM training in Singapore. We have a wide range of BIM & CAD training courses that will help you learn more about BIM for construction design.",
    "contactPoint": [
        {
            "@type": "ContactPoint",
            "telephone": "+65 6455 9938",
            "contactType": "customer service",
            "email": "training@aia.edu.sg"
        }
    ],
    "address": {
        "@type": "PostalAddress",
        "streetAddress": "200 Braddell Road, Block B, #01-09, BCA Braddell Campus",
        "addressLocality": "Singapore",
        "postalCode": "579700",
        "addressCountry": "SG"
    }
}

export function getOrganizationJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        ...ORG_BASE,
        ...overrides
    }
}

export function getAboutPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": overrides.name || "BIM & CAD Training Center | Ace Industrial Academy",
        "description": overrides.description || "Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more.",
        "url": overrides.url || "https://www.aia.edu.sg/aboutaia",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getContactPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "name": overrides.name || "Contact Us | Ace Industrial Academy",
        "description": overrides.description || "Contact Us | Ace Industrial Academy",
        "url": overrides.url || "https://www.aia.edu.sg/contact",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getFaqPageJsonLd(faqs = [], overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
            }
        })),
        ...overrides
    }
}

export function getBlogCollectionJsonLd(posts = [], overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": overrides.name || "All BIM & CAD Modeling Courses | BIM & CAD Training Center",
        "description": overrides.description || "Ace Industrial Academy conducts BIM courses for Autocad 2D/3D, Revit, Synchro 4D, OpenPlant, and more",
        "hasPart": posts.map(post => ({
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.description,
            "url": `https://www.aia.edu.sg/blog/${post.slug}`,
            ...(post.coverPhoto?.fluid?.src && {
                "image": post.coverPhoto.fluid.src.startsWith("http")
                    ? post.coverPhoto.fluid.src
                    : `https:${post.coverPhoto.fluid.src}`
            })
        })),
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getCourseItemListJsonLd(items = [], {
    maxItems = null,
    baseUrl = "https://www.aia.edu.sg/courses/",
    listName = "Featured BIM & CAD Courses at Ace Industrial Academy",
    overrides = {}
} = {}) {
    const list = maxItems ? items.slice(0, maxItems) : items;

    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": overrides.name || listName,
        "itemListElement": list.map((item, index) => {
            const durationHours = parseFloat(item.duration);
            const isoDuration = isNaN(durationHours)
                ? undefined
                : `PT${durationHours}H`;

            const isClassroom = item.trainingMode === "Classroom";
            const courseMode = isClassroom ? "Onsite" : (item.trainingMode ?? "Onsite");

            const location = isClassroom
                ? {
                    "@type": "Place",
                    "name": ORG_BASE.address.addressLocality,
                    "address": `${ORG_BASE.address.streetAddress}, ${ORG_BASE.address.addressLocality} ${ORG_BASE.address.postalCode}`
                }
                : undefined;

            const courseObj = {
                "@type": "Course",
                "name": item.title,
                "description": item.description || "This course provides professional training in BIM & CAD.",
                "url": `${baseUrl}${item.slug}`,
                "provider": {
                    "@type": "Organization",
                    "@id": ORG_BASE["@id"],
                    "name": ORG_BASE["name"],
                    "url": ORG_BASE["url"]
                }
            };

            if (item.product?.price || item.price) {
                courseObj.offers = {
                    "@type": "Offer",
                    "price": item.product?.price || item.price,
                    "priceCurrency": "SGD",
                    "availability": "https://schema.org/InStock",
                    ...(item.category && { "category": item.category.join(', ') })
                };
            }

            courseObj.hasCourseInstance = {
                "@type": "CourseInstance",
                "courseMode": courseMode,
                ...(location && { location }),
                ...(isoDuration && { courseWorkload: isoDuration })
            };

            return {
                "@type": "ListItem",
                "position": index + 1,
                "item": courseObj
            };
        }),
        ...overrides
    };
}

export function getFundingPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "SSG Funding | Ace Industrial Academy",
        "description": overrides.description || "Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more.",
        "url": overrides.url || "https://www.aia.edu.sg/funding",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getForLearnersPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": overrides.name || "For Learners | Ace Industrial Academy",
        "description": overrides.description || "Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more.",
        "url": overrides.url || "https://www.aia.edu.sg/forlearners",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getAppealFormPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Appeal Form | Ace Industrial Academy",
        "description": overrides.description || "Submit your appeal for BIM & CAD training courses at Ace Industrial Academy.",
        "url": overrides.url || "https://www.aia.edu.sg/AppealForm",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getLoginPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Login | Ace Industrial Academy",
        "description": overrides.description || "Login to your Ace Industrial Academy account to access BIM & CAD training resources.",
        "url": overrides.url || "https://www.aia.edu.sg/login",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getRegistrationPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Course Registration | Ace Industrial Academy",
        "description": overrides.description || "Register for BIM & CAD training courses at Ace Industrial Academy.",
        "url": overrides.url || "https://www.aia.edu.sg/registration",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getReschedulePageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Reschedule & Withdrawal Policy | Ace Industrial Academy",
        "description": overrides.description || "Learn about the rescheduling and withdrawal policy for courses at Ace Industrial Academy.",
        "url": overrides.url || "https://www.aia.edu.sg/reschedule",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getPseaPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Post-Secondary Education Account (PSEA) | Ace Industrial Academy",
        "description": overrides.description || "Information about eligibility for the Post-Secondary Education Account (PSEA) scheme",
        "url": overrides.url || "https://www.aia.edu.sg/PSEA",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getSfcPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "SkillsFuture Credit (SFC) | Ace Industrial Academy",
        "description": overrides.description || "Guide to claiming SkillsFuture Credit for WSQ courses at Ace Industrial Academy.",
        "url": overrides.url || "https://www.aia.edu.sg/SFC",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getShortCoursesPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Short Courses | Ace Industrial Academy",
        "description": overrides.description || "Explore short courses at Ace Industrial Academy.",
        "url": overrides.url || "https://www.aia.edu.sg/short-courses",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getWhyAiaPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": overrides.name || "Why Ace Industrial Academy | Ace Industrial Academy",
        "description": overrides.description || "Award-Winning Training Excellence at Ace Industrial Academy.",
        "url": overrides.url || "https://www.aia.edu.sg/whyaia",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getWithdrawalFormPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Withdrawal Form | Ace Industrial Academy",
        "description": overrides.description || "Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more.",
        "url": overrides.url || "https://www.aia.edu.sg/WithdrawalForm",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getBlogPostJsonLd(post, overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title || overrides.title,
        "description": post.description || overrides.description,
        "url": `https://www.aia.edu.sg/blog/${post.slug}`,
        "publisher": { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getCourseJsonLd(course, {
    baseUrl = "https://www.aia.edu.sg/courses/",
    overrides = {}
} = {}) {
    const durationHours = parseFloat(course.duration);
    const isoDuration = isNaN(durationHours)
        ? undefined
        : `PT${durationHours}H`;

    const isClassroom = course.trainingMode === "Classroom";
    const courseMode = isClassroom ? "Onsite" : (course.trainingMode ?? "Onsite");

    const location = isClassroom
        ? {
            "@type": "Place",
            "name": ORG_BASE.address.addressLocality,
            "address": `${ORG_BASE.address.streetAddress}, ${ORG_BASE.address.addressLocality} ${ORG_BASE.address.postalCode}`
        }
        : undefined;

    const courseObj = {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": course.title,
        "description": course.description || "This course provides professional training in BIM & CAD.",
        "url": `${baseUrl}${course.slug}`,
        "provider": {
            "@type": "Organization",
            "@id": ORG_BASE["@id"],
            "name": ORG_BASE["name"],
            "url": ORG_BASE["url"]
        }
    };

    if (course.product?.price || course.price) {
        courseObj["offers"] = {
            "@type": "Offer",
            "price": course.product?.price || course.price,
            "priceCurrency": "SGD",
            "availability": "https://schema.org/InStock",
            ...(course.category && { "category": course.category.join(', ') })
        };
    }

    courseObj["hasCourseInstance"] = {
        "@type": "CourseInstance",
        "courseMode": courseMode,
        ...(location && { "location": location }),
        ...(isoDuration && { "courseWorkload": isoDuration })
    };

    return {
        ...courseObj,
        ...overrides
    };
}

export function getTheTeamPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": overrides.name || "Meet The Team | Ace Industrial Academy",
        "description": overrides.description || "Ace Industrial Academy Organizational Structure.",
        "url": overrides.url || "https://www.aia.edu.sg/theteam",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}

export function getScheduleFormPageJsonLd(overrides = {}) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": overrides.name || "Schedule Form | Ace Industrial Academy",
        "description": overrides.description || "Schedule your BIM & CAD training at Ace Industrial Academy.",
        "url": overrides.url || "https://www.aia.edu.sg/ScheduleForm",
        "publisher": overrides.publisher || { "@id": ORG_BASE["@id"] },
        ...overrides
    }
}